import { db, getDeviceId } from "../db/database";
import { GenericAdapter } from "../imports/GenericAdapter";
import type {
  AuditLog,
  InventoryBalance,
  MarketplaceListing,
  MarketplaceVariantListing,
  PendingSync,
  Product,
  ProductImportBatch,
  ProductVariant,
  VariantAlias,
} from "../types";
import { cleanText, displayColor, normalizeText, optionalNumber } from "../utils/normalize";
import { randomId } from "../utils/id";
import { getSession } from "./auth";

const LISTING_COLUMNS = [
  "Nome da sua loja", "ID de Anúncio", "Nome do Anúncio", "SKU Principal", "Descrição", "Categoria ID",
  "Preço", "Preço com Desconto", "Guia de Tamanhos", "Imagem de Capa", "Imagem de Anúncio1",
  "Imagem de Anúncio2", "Imagem de Anúncio3", "Imagem de Anúncio4", "Imagem de Anúncio5",
  "Imagem de Anúncio6", "Imagem de Anúncio7", "Imagem de Anúncio8", "Imagem de Anúncio9",
  "Peso (kg)", "Comprimento (cm)", "Largura (cm)", "Altura (cm)",
  "O prazo de entrega da Pré-venda （Dias)", "Link do Fornecedor", "Data de Criação", "Horário Atualizado",
] as const;

const REQUIRED_COLUMNS = ["ID de Anúncio", "Nome do Anúncio", "SKU Principal", "Opção por Variante1"] as const;
const CARRY_ACROSS_LISTINGS = new Set<string>(["Nome da sua loja", "SKU Principal"]);

export interface ShopeeVariantDraft {
  sourceRowNumber: number;
  colorName: string;
  sizeName?: string;
  externalVariantId?: string;
  marketplaceSku?: string;
  imageUrl?: string;
  listedQuantity?: number;
  promotionQuantity?: number;
  gtin?: string;
  price?: number;
  discountPrice?: number;
  rawData: Record<string, unknown>;
}

export interface ShopeeListingDraft {
  externalListingId: string;
  shopName?: string;
  listingName: string;
  description?: string;
  categoryExternalId?: string;
  coverImageUrl?: string;
  imageUrls: string[];
  price?: number;
  discountPrice?: number;
  sizeGuide?: string;
  weightKg?: number;
  lengthCm?: number;
  widthCm?: number;
  heightCm?: number;
  preorderDays?: number;
  supplierLink?: string;
  sourceCreatedAt?: string;
  sourceUpdatedAt?: string;
  rawData: Record<string, unknown>;
  variants: ShopeeVariantDraft[];
}

export interface ShopeeProductPreview {
  sku: string;
  name: string;
  description?: string;
  categoryExternalId?: string;
  coverImageUrl?: string;
  colors: string[];
  listings: ShopeeListingDraft[];
  warnings: string[];
  action: "novo" | "atualizar";
}

export interface ShopeeProductFile {
  fileName: string;
  fileHash: string;
  rowCount: number;
  totalProductsInFile: number;
  alreadyImportedCount: number;
  resumedImport: boolean;
  products: ShopeeProductPreview[];
  warnings: string[];
}

function rawRecord(row: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(row).map(([key, value]) => [key, value instanceof Date ? value.toISOString() : value]));
}

function value(row: Record<string, unknown>, column: string): string {
  return cleanText(row[column]);
}

function listingFromRow(row: Record<string, unknown>, rawData: Record<string, unknown>): ShopeeListingDraft {
  return {
    externalListingId: value(row, "ID de Anúncio"),
    shopName: value(row, "Nome da sua loja") || undefined,
    listingName: value(row, "Nome do Anúncio") || value(row, "SKU Principal"),
    description: value(row, "Descrição") || undefined,
    categoryExternalId: value(row, "Categoria ID") || undefined,
    coverImageUrl: value(row, "Imagem de Capa") || undefined,
    imageUrls: Array.from({ length: 9 }, (_, index) => value(row, `Imagem de Anúncio${index + 1}`)).filter(Boolean),
    price: optionalNumber(row["Preço"]),
    discountPrice: optionalNumber(row["Preço com Desconto"]),
    sizeGuide: value(row, "Guia de Tamanhos") || undefined,
    weightKg: optionalNumber(row["Peso (kg)"]),
    lengthCm: optionalNumber(row["Comprimento (cm)"]),
    widthCm: optionalNumber(row["Largura (cm)"]),
    heightCm: optionalNumber(row["Altura (cm)"]),
    preorderDays: optionalNumber(row["O prazo de entrega da Pré-venda （Dias)"]),
    supplierLink: value(row, "Link do Fornecedor") || undefined,
    sourceCreatedAt: value(row, "Data de Criação") || undefined,
    sourceUpdatedAt: value(row, "Horário Atualizado") || undefined,
    rawData,
    variants: [],
  };
}

export async function parseShopeeProductFile(file: File): Promise<ShopeeProductFile> {
  const parsed = await new GenericAdapter().parse(file);
  const missing = REQUIRED_COLUMNS.filter((column) => !parsed.headers.includes(column));
  if (missing.length) throw new Error(`A planilha não parece ser uma exportação de anúncios da Shopee. Colunas ausentes: ${missing.join(", ")}.`);
  const previousImport = await db.productImports.where("fileHash").equals(parsed.fileHash).first();

  const carrier: Record<string, unknown> = {};
  let currentListingId = "";
  const grouped = new Map<string, { sku: string; listings: Map<string, ShopeeListingDraft>; colors: Map<string, string>; warnings: string[] }>();
  const warnings: string[] = [];

  parsed.rows.forEach((sourceRow, index) => {
    const sourceListingId = value(sourceRow, "ID de Anúncio");
    if (sourceListingId && sourceListingId !== currentListingId) {
      currentListingId = sourceListingId;
      for (const column of LISTING_COLUMNS) if (!CARRY_ACROSS_LISTINGS.has(column)) delete carrier[column];
    }
    for (const column of LISTING_COLUMNS) {
      if (sourceRow[column] !== "" && sourceRow[column] !== null && sourceRow[column] !== undefined) carrier[column] = sourceRow[column];
    }
    const row = { ...sourceRow, ...carrier };
    const sku = value(row, "SKU Principal").toUpperCase();
    const listingId = value(row, "ID de Anúncio");
    const colorName = displayColor(row["Opção por Variante1"]);
    if (!sku || !listingId || !colorName) {
      warnings.push(`Linha ${index + 2} ignorada: faltou SKU principal, anúncio ou cor.`);
      return;
    }
    const productGroup = grouped.get(normalizeText(sku)) ?? { sku, listings: new Map(), colors: new Map(), warnings: [] };
    const rawData = rawRecord(sourceRow);
    const listing = productGroup.listings.get(listingId) ?? listingFromRow(row, rawData);
    listing.variants.push({
      sourceRowNumber: index + 2,
      colorName,
      sizeName: value(row, "Opção por Variante2") || undefined,
      externalVariantId: value(row, "ID da Variante") || undefined,
      marketplaceSku: value(row, "SKU") || undefined,
      imageUrl: value(row, "Imagem por Variante") || undefined,
      listedQuantity: optionalNumber(row["Quantidade"]),
      promotionQuantity: optionalNumber(row["Qtd. de Promoção"]),
      gtin: value(row, "GTIN") || undefined,
      price: optionalNumber(row["Preço"]),
      discountPrice: optionalNumber(row["Preço com Desconto"]),
      rawData,
    });
    productGroup.listings.set(listingId, listing);
    if (!productGroup.colors.has(normalizeText(colorName))) productGroup.colors.set(normalizeText(colorName), colorName);
    grouped.set(normalizeText(sku), productGroup);
  });

  const [currentProducts, importedListings] = await Promise.all([
    db.products.toArray(),
    db.marketplaceListings.where("marketplace").equals("shopee").toArray(),
  ]);
  const currentSku = new Set(currentProducts.map((product) => normalizeText(product.sku)));
  const importedListingIds = new Set(importedListings.map((listing) => listing.externalListingId));
  const allProducts = Array.from(grouped.values()).map<ShopeeProductPreview>((group) => {
    const listings = Array.from(group.listings.values());
    const names = new Set(listings.map((listing) => listing.listingName));
    if (names.size > 1) group.warnings.push(`${names.size} anúncios diferentes usam o SKU principal ${group.sku}; todos ficarão reunidos no mesmo produto.`);
    const primary = listings[0];
    return {
      sku: group.sku,
      name: primary?.listingName || group.sku,
      description: primary?.description,
      categoryExternalId: primary?.categoryExternalId,
      coverImageUrl: primary?.coverImageUrl,
      colors: Array.from(group.colors.values()).sort((a, b) => a.localeCompare(b, "pt-BR")),
      listings,
      warnings: group.warnings,
      action: currentSku.has(normalizeText(group.sku)) ? "atualizar" : "novo",
    };
  }).sort((a, b) => a.sku.localeCompare(b.sku, "pt-BR", { numeric: true }));

  if (!allProducts.length) throw new Error("Nenhum produto válido foi encontrado na planilha.");
  const products = allProducts.filter((product) => product.listings.some((listing) => !importedListingIds.has(listing.externalListingId)));
  return {
    fileName: parsed.fileName,
    fileHash: parsed.fileHash,
    rowCount: parsed.rows.length,
    totalProductsInFile: allProducts.length,
    alreadyImportedCount: allProducts.length - products.length,
    resumedImport: Boolean(previousImport),
    products,
    warnings,
  };
}

function stableListingId(externalListingId: string) {
  return `shopee_listing_${externalListingId}`;
}

function stableMarketplaceVariantId(listingId: string, draft: ShopeeVariantDraft) {
  if (draft.externalVariantId) return `shopee_variant_${draft.externalVariantId}`;
  return `shopee_variant_${listingId}_${normalizeText(`${draft.colorName}_${draft.sizeName ?? ""}`).slice(0, 36)}`;
}

export async function importSelectedShopeeProducts(file: ShopeeProductFile, selectedSkus: ReadonlySet<string>): Promise<ProductImportBatch> {
  const selected = file.products.filter((product) => selectedSkus.has(product.sku));
  if (!selected.length) throw new Error("Selecione pelo menos um produto para importar.");

  const timestamp = new Date().toISOString();
  const session = await getSession();
  const user = session?.user ?? { id: "offline-user", name: "Usuário offline" };
  const deviceId = await getDeviceId();
  const settings = await db.settings.get("app");
  const previousBatch = await db.productImports.where("fileHash").equals(file.fileHash).first();
  const importedListings = selected.reduce((sum, product) => sum + product.listings.length, 0);
  const importedVariants = selected.reduce((sum, product) => sum + product.listings.reduce((count, listing) => count + listing.variants.length, 0), 0);
  const importedWarnings = file.warnings.length + selected.reduce((sum, product) => sum + product.warnings.length, 0);
  const batch: ProductImportBatch = {
    id: previousBatch?.id ?? randomId("product_import"), source: "shopee", fileName: file.fileName, fileHash: file.fileHash,
    status: "concluido", productsCount: (previousBatch?.productsCount ?? 0) + selected.length,
    listingsCount: (previousBatch?.listingsCount ?? 0) + importedListings,
    variantsCount: (previousBatch?.variantsCount ?? 0) + importedVariants,
    warningsCount: (previousBatch?.warningsCount ?? 0) + importedWarnings,
    userName: user.name, createdAt: previousBatch?.createdAt ?? timestamp,
  };

  const tables = [db.products, db.variants, db.balances, db.variantAliases, db.marketplaceListings, db.marketplaceVariants, db.productImports, db.pendingSync, db.auditLogs];
  await db.transaction("rw", tables, async () => {
    await db.productImports.put(batch);
    const allProducts = await db.products.toArray();
    const productBySku = new Map(allProducts.map((product) => [normalizeText(product.sku), product]));

    for (const source of selected) {
      const existing = productBySku.get(normalizeText(source.sku));
      const product: Product = existing ? {
        ...existing,
        photoUrl: existing.photoUrl || source.coverImageUrl,
        updatedAt: timestamp,
      } : {
        id: randomId("product"), name: source.name, sku: source.sku,
        category: source.categoryExternalId ? `Shopee ${source.categoryExternalId}` : "Shopee",
        description: source.description, photoUrl: source.coverImageUrl,
        status: "ativo", notes: "Produto importado dos anúncios da Shopee.",
        createdAt: timestamp, updatedAt: timestamp,
      };
      await db.products.put(product);
      productBySku.set(normalizeText(product.sku), product);

      const storedVariants = await db.variants.where("productId").equals(product.id).toArray();
      const variantByColor = new Map(storedVariants.map((variant) => [normalizeText(variant.colorName), variant]));
      const createdVariants: ProductVariant[] = [];
      const createdBalances: InventoryBalance[] = [];
      for (const colorName of source.colors) {
        const colorKey = normalizeText(colorName);
        if (variantByColor.has(colorKey)) continue;
        const variant: ProductVariant = {
          id: randomId("variant"), productId: product.id, colorName, minimumStock: settings?.defaultMinimumStock ?? 10,
          malhariaEnabled: false, status: "ativo", updatedAt: timestamp,
        };
        const balance: InventoryBalance = {
          id: `${variant.id}:galpao`, variantId: variant.id, warehouse: "galpao", quantity: 0, version: 1, updatedAt: timestamp,
        };
        createdVariants.push(variant);
        createdBalances.push(balance);
        variantByColor.set(colorKey, variant);
      }
      if (createdVariants.length) await db.variants.bulkAdd(createdVariants);
      if (createdBalances.length) await db.balances.bulkAdd(createdBalances);

      const storedAliases = await db.variantAliases.where("productId").equals(product.id).toArray();
      const aliasByName = new Map(storedAliases.map((alias) => [alias.normalizedAlias, alias]));
      const aliases: VariantAlias[] = [];
      const listings: MarketplaceListing[] = [];
      const marketplaceVariants: MarketplaceVariantListing[] = [];

      for (const listingDraft of source.listings) {
        const listingId = stableListingId(listingDraft.externalListingId);
        listings.push({
          id: listingId, productId: product.id, importId: batch.id, marketplace: "shopee",
          shopName: listingDraft.shopName, externalListingId: listingDraft.externalListingId,
          listingName: listingDraft.listingName, description: listingDraft.description,
          categoryExternalId: listingDraft.categoryExternalId, coverImageUrl: listingDraft.coverImageUrl,
          imageUrls: listingDraft.imageUrls, price: listingDraft.price, discountPrice: listingDraft.discountPrice,
          sizeGuide: listingDraft.sizeGuide, weightKg: listingDraft.weightKg, lengthCm: listingDraft.lengthCm,
          widthCm: listingDraft.widthCm, heightCm: listingDraft.heightCm, preorderDays: listingDraft.preorderDays,
          supplierLink: listingDraft.supplierLink, sourceCreatedAt: listingDraft.sourceCreatedAt,
          sourceUpdatedAt: listingDraft.sourceUpdatedAt, rawData: listingDraft.rawData,
          importedAt: timestamp, updatedAt: timestamp,
        });
        for (const variantDraft of listingDraft.variants) {
          const variant = variantByColor.get(normalizeText(variantDraft.colorName));
          marketplaceVariants.push({
            id: stableMarketplaceVariantId(listingDraft.externalListingId, variantDraft), listingId, productId: product.id,
            variantId: variant?.id, externalVariantId: variantDraft.externalVariantId,
            marketplaceSku: variantDraft.marketplaceSku, colorName: variantDraft.colorName, sizeName: variantDraft.sizeName,
            imageUrl: variantDraft.imageUrl, listedQuantity: variantDraft.listedQuantity,
            promotionQuantity: variantDraft.promotionQuantity, gtin: variantDraft.gtin,
            price: variantDraft.price, discountPrice: variantDraft.discountPrice,
            rawData: variantDraft.rawData, updatedAt: timestamp,
          });
          if (!variant) continue;
          const candidates = [variantDraft.marketplaceSku, `${product.sku}-${variantDraft.colorName}`].filter((candidate): candidate is string => Boolean(candidate?.trim()));
          for (const aliasText of candidates) {
            const normalizedAlias = normalizeText(aliasText);
            if (!normalizedAlias || aliasByName.has(normalizedAlias)) continue;
            const alias: VariantAlias = {
              id: randomId("alias"), productId: product.id, variantId: variant.id, alias: aliasText,
              normalizedAlias, kind: "sku", source: "shopee", createdAt: timestamp, updatedAt: timestamp,
            };
            aliasByName.set(normalizedAlias, alias);
            aliases.push(alias);
          }
        }
      }
      if (aliases.length) await db.variantAliases.bulkAdd(aliases);
      if (listings.length) await db.marketplaceListings.bulkPut(listings);
      if (marketplaceVariants.length) await db.marketplaceVariants.bulkPut(marketplaceVariants);

      const operation: PendingSync = {
        id: randomId("sync"), operationType: "product_upsert",
        payload: { product, variants: createdVariants, balances: createdBalances, aliases, listings, marketplaceVariants, productImport: batch },
        deviceId, userId: user.id, status: "pendente", attempts: 0, createdAt: timestamp, updatedAt: timestamp,
      };
      const audit: AuditLog = {
        id: randomId("audit"), action: existing ? "Produto atualizado por importação Shopee" : "Produto importado da Shopee",
        entityType: "product", entityId: product.id, before: existing,
        after: { product, colorsImported: source.colors, listingsImported: listings.length },
        userId: user.id, userName: user.name, deviceId, createdAt: timestamp,
      };
      await db.pendingSync.add(operation);
      await db.auditLogs.add(audit);
    }
  });
  return batch;
}
